import { z } from 'zod'

const requestBodySchema = z.object({
  requestId: z.string().min(8),
  text: z.string().optional(),
  mode: z.string().optional(),
  command: z.string().optional(),
  messageId: z.string().optional(),
  kind: z.literal('continue').optional(),
  directive: z.unknown().optional(),
  inputType: z.enum(['dialogue', 'ooc']).optional(),
  applyTo: z.string().optional(),
  reasoning: z.enum(['fast', 'deep']).optional(),
})

const pendingRequestSchema = z.object({
  roomId: z.string().min(1),
  path: z.enum([
    'turn',
    'continue',
    'command',
    'regenerate',
    'snap',
    'suggestion',
  ]),
  body: requestBodySchema,
  operationId: z.string().min(1).nullable(),
  model: z.string().optional(),
  preset: z.string().optional(),
  presetVersion: z.number().optional(),
  status: z
    .enum(['submitting', 'queued', 'running', 'failed', 'unknown'])
    .optional(),
  retryable: z.boolean().optional(),
  rejectionCode: z
    .enum([
      'INSUFFICIENT_FUNDS',
      'CSRF_PREPARATION_FAILED',
      'INVALID_CSRF_RESPONSE',
    ])
    .optional(),
  canReissue: z.boolean().optional(),
})

export type PendingRequest = z.infer<typeof pendingRequestSchema>

/** 이전 저장 형식은 본문 스냅샷과 작업 ID가 없으므로 조회 복구에만 쓰고 재전송하지 않는다. */
export function readPendingRequest(
  value: unknown,
  roomId: string
): PendingRequest | null {
  const current = pendingRequestSchema.safeParse(value)
  if (current.success) {
    if (current.data.roomId !== roomId) return null
    // Retired OOC requests remain queryable by operation ID but cannot be reissued.
    if (
      current.data.body.inputType === 'ooc' ||
      current.data.body.applyTo !== undefined
    )
      return { ...current.data, canReissue: false }
    return current.data
  }
  if (!value || typeof value !== 'object') return null
  const legacy = value as Record<string, unknown>
  if (typeof legacy.requestId !== 'string' || legacy.requestId.length < 8)
    return null
  const path =
    typeof legacy.command === 'string'
      ? 'command'
      : legacy.kind === 'continue'
        ? 'continue'
        : 'turn'
  const body = requestBodySchema.safeParse(legacy)
  return body.success
    ? { roomId, path, body: body.data, operationId: null, canReissue: false }
    : null
}
